import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, BookOpen, Loader2, Trash2, Pencil, Printer, X, Search, GripVertical, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";

const AGE_GROUPS = [
  { id: "infant", label: "Infant" },
  { id: "toddler_young", label: "Young Toddler" },
  { id: "toddler_older", label: "Older Toddler" },
  { id: "preschool_25_3", label: "Preschool 2.5–3" },
  { id: "preschool_3_4", label: "Preschool 3–4" },
  { id: "preschool_4_5", label: "Preschool 4–5" },
  { id: "school_age", label: "School-age" },
];

const DAYS = [
  { id: "monday", label: "Monday" },
  { id: "tuesday", label: "Tuesday" },
  { id: "wednesday", label: "Wednesday" },
  { id: "thursday", label: "Thursday" },
  { id: "friday", label: "Friday" },
];

// Default starter rows for a new plan. Teachers can rename, add, or delete.
const DEFAULT_BLOCKS = () => [
  { time: "8:30–9:00", activity: "" },
  { time: "9:00–10:00", activity: "" },
  { time: "10:00–11:00", activity: "" },
  { time: "11:00–12:00", activity: "" },
  { time: "1:00–2:00", activity: "" },
];

// Returns the Monday of the same week as `input` as a YYYY-MM-DD string.
// Accepts either a Date object or a YYYY-MM-DD string. Pure local-calendar
// math — never round-trips through UTC — so the result lands on the correct
// Monday regardless of the user's timezone. Earlier versions used
// `new Date("YYYY-MM-DD")` (parsed as UTC) + `toISOString()` which off-by-one'd
// the result by a day for users east-of-UTC after midnight or west-of-UTC
// before midnight, producing a Tuesday-when-Monday-was-wanted bug.
const mondayOf = (input) => {
  let y, m, d;
  if (input instanceof Date) {
    y = input.getFullYear(); m = input.getMonth(); d = input.getDate();
  } else if (typeof input === "string") {
    const parts = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!parts) return input;
    y = +parts[1]; m = +parts[2] - 1; d = +parts[3];
  } else {
    const now = new Date();
    y = now.getFullYear(); m = now.getMonth(); d = now.getDate();
  }
  const dt = new Date(y, m, d);              // local midnight — no UTC drift
  const day = dt.getDay() || 7;              // Sun=0 → 7 so Sunday → previous Monday
  dt.setDate(dt.getDate() - day + 1);        // back to that week's Monday
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// Local-calendar weekday strip. Given a Monday in YYYY-MM-DD, render the five
// teaching days (Mon→Fri) of that week so the director can see exactly which
// week they're about to plan for. The day matching `highlightIso` (or today,
// when it falls inside the displayed week) gets a green ring.
function WeekPreview({ mondayIso, highlightIso }) {
  if (!mondayIso) return null;
  const parts = mondayIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!parts) return null;
  const baseY = +parts[1], baseM = +parts[2] - 1, baseD = +parts[3];
  const todayLocal = new Date();
  const todayIso = `${todayLocal.getFullYear()}-${String(todayLocal.getMonth() + 1).padStart(2, "0")}-${String(todayLocal.getDate()).padStart(2, "0")}`;
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const days = labels.map((label, i) => {
    const dt = new Date(baseY, baseM, baseD + i);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    return { label, iso, dom: dt.getDate(), monthShort: dt.toLocaleDateString(undefined, { month: "short" }) };
  });
  const target = highlightIso || todayIso;
  return (
    <div data-testid="lp-week-preview" className="mt-2 rounded-xl border border-stone-200 bg-stone-50/60 p-2">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 px-1 flex items-center justify-between">
        <span>This week&apos;s plan covers</span>
        <span className="text-stone-400 text-[10px] normal-case tracking-normal italic">Mon → Fri</span>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {days.map((d) => {
          const isTarget = d.iso === target;
          const isToday = d.iso === todayIso;
          return (
            <div
              key={d.iso}
              data-testid={`lp-week-day-${d.label.toLowerCase()}`}
              className={`relative rounded-lg px-1 py-2 text-center transition ${
                isTarget
                  ? "bg-emerald-700 text-white ring-2 ring-emerald-700"
                  : isToday
                  ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200"
                  : "bg-white text-stone-700 border border-stone-200"
              }`}
            >
              <div className={`text-[9px] uppercase tracking-widest font-bold ${isTarget ? "opacity-90" : "opacity-60"}`}>{d.label}</div>
              <div className="font-display text-lg leading-none font-semibold">{d.dom}</div>
              <div className={`text-[9px] mt-0.5 ${isTarget ? "opacity-80" : "opacity-50"}`}>{d.monthShort}</div>
              {isToday && !isTarget && (
                <div className="absolute -top-1.5 right-1 text-[8px] font-bold uppercase tracking-widest bg-emerald-600 text-white px-1 py-0.5 rounded-full">Today</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const emptyPlan = () => ({
  title: "",
  week_of: mondayOf(new Date()),
  age_group: "preschool_3_4",
  classroom: "",
  objectives: "",
  activities: "",
  materials: "",
  elds_alignment: "",
  schedule: DAYS.reduce((acc, d) => ({ ...acc, [d.id]: DEFAULT_BLOCKS() }), {}),
});

export default function LessonPlans() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // plan_id | null
  const [draft, setDraft] = useState(emptyPlan());
  const [elds, setElds] = useState({ domains: [], standards: [] });
  const [genOpen, setGenOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/lesson-plans").then((r) => setList(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/elds-standards").then((r) => setElds(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setDraft(emptyPlan());
    setOpen(true);
  };

  // Launch the editor with an AI-generated starter plan already populated.
  const openWithSuggestion = (suggestion, seed) => {
    setEditing(null);
    setDraft({
      ...emptyPlan(),
      title: suggestion.title || "",
      week_of: seed.week_of || mondayOf(new Date()),
      age_group: seed.age_group || "preschool_3_4",
      classroom: seed.classroom || "",
      objectives: suggestion.objectives || "",
      materials: suggestion.materials || "",
      schedule: DAYS.reduce((acc, d) => ({
        ...acc,
        [d.id]: (suggestion.schedule?.[d.id] || []).map((b) => ({
          time: b.time || "",
          activity: b.activity || "",
          elds_codes: b.elds_codes || [],
        })),
      }), {}),
    });
    setGenOpen(false);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    // Back-compat: if the saved plan has no schedule, seed an empty grid.
    const schedule = p.schedule && Object.keys(p.schedule).length
      ? DAYS.reduce((acc, d) => ({ ...acc, [d.id]: (p.schedule[d.id] || []).map((b) => ({ time: b.time || "", activity: b.activity || "", elds_codes: b.elds_codes || [] })) }), {})
      : DAYS.reduce((acc, d) => ({ ...acc, [d.id]: DEFAULT_BLOCKS() }), {});
    setDraft({
      title: p.title || "",
      week_of: p.week_of || mondayOf(new Date()),
      age_group: p.age_group || "preschool_3_4",
      classroom: p.classroom || "",
      objectives: p.objectives || "",
      activities: p.activities || "",
      materials: p.materials || "",
      elds_alignment: p.elds_alignment || "",
      schedule,
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lesson plan?")) return;
    try { await api.delete(`/lesson-plans/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const printPdf = async (id) => {
    try {
      const res = await api.get(`/lesson-plans/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="lesson-plans-page" className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Curriculum · Ohio ELDS</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Lesson plans</h1>
          <p className="mt-2 text-stone-500">Build weekly plans aligned to Ohio Early Learning &amp; Development Standards, then print the weekly grid.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="lp-batch-btn" onClick={() => setBatchOpen(true)} variant="outline" className="rounded-xl h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <Sparkles className="w-4 h-4 mr-1" /> Plan my whole center
          </Button>
          <Button data-testid="lp-generate-btn" onClick={() => setGenOpen(true)} variant="outline" className="rounded-xl h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <Sparkles className="w-4 h-4 mr-1" /> Generate starter plan
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="lp-add-btn" onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                <Plus className="w-4 h-4 mr-1" /> New lesson plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit lesson plan" : "New lesson plan"}</DialogTitle>
                <DialogDescription>Fill the Mon-Fri grid. Tag each activity with Ohio ELDS indicators.</DialogDescription>
              </DialogHeader>
              <LessonPlanForm
                draft={draft}
                setDraft={setDraft}
                elds={elds}
                editing={editing}
                onSaved={() => { setOpen(false); load(); }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <LessonPlanGeneratorDialog open={genOpen} onClose={() => setGenOpen(false)} onUse={openWithSuggestion} />
      <LessonPlanBatchDialog open={batchOpen} onClose={() => setBatchOpen(false)} onCreated={load} />

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-14 text-center">
          <BookOpen className="w-8 h-8 mx-auto text-stone-300" />
          <p className="mt-3 font-display text-xl text-stone-900">No lesson plans yet.</p>
          <p className="mt-1 text-sm text-stone-500">Build your first Mon-Fri plan — pick ELDS indicators per activity and print the grid.</p>
          <Button onClick={openNew} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
            <Plus className="w-4 h-4 mr-1" /> Start a weekly plan
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lp-grid">
          {list.map((p) => {
            const totalBlocks = Object.values(p.schedule || {}).flat().filter((b) => (b.activity || "").trim()).length;
            const tagged = Object.values(p.schedule || {}).flat().flatMap((b) => b.elds_codes || []);
            const uniqueCodes = Array.from(new Set(tagged));
            return (
              <article key={p.id} data-testid={`lp-card-${p.id}`} className="rounded-2xl border border-stone-200 bg-white p-6 hover:shadow-lift transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Week of {fmtDate(p.week_of)}</div>
                    <h3 className="mt-1 font-display text-lg font-medium text-stone-900">{p.title}</h3>
                    <div className="mt-1 text-xs text-stone-500">
                      {AGE_GROUPS.find((g) => g.id === p.age_group)?.label || p.age_group}
                      {p.classroom && <> · {p.classroom}</>}
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} data-testid={`lp-delete-${p.id}`} className="text-stone-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {p.objectives && <p className="mt-3 text-sm text-stone-700 line-clamp-3">{p.objectives}</p>}

                <div className="mt-3 flex items-center flex-wrap gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                    {totalBlocks} activities
                  </span>
                  {uniqueCodes.length > 0 && (
                    <span className="px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200 font-semibold">
                      {uniqueCodes.length} ELDS tags
                    </span>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button data-testid={`lp-edit-${p.id}`} variant="outline" size="sm" onClick={() => openEdit(p)} className="rounded-xl">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button data-testid={`lp-print-${p.id}`} variant="outline" size="sm" onClick={() => printPdf(p.id)} className="rounded-xl">
                    <Printer className="w-3.5 h-3.5 mr-1" /> Print
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LessonPlanForm({ draft, setDraft, elds, editing, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [eldsPickerFor, setEldsPickerFor] = useState(null); // {day, idx}

  const up = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateBlock = (day, idx, patch) =>
    setDraft((d) => ({
      ...d,
      schedule: {
        ...d.schedule,
        [day]: (d.schedule[day] || []).map((b, i) => (i === idx ? { ...b, ...patch } : b)),
      },
    }));
  const addBlockRow = () =>
    setDraft((d) => ({
      ...d,
      schedule: DAYS.reduce(
        (acc, day) => ({ ...acc, [day.id]: [...(d.schedule[day.id] || []), { time: "", activity: "", elds_codes: [] }] }),
        {},
      ),
    }));
  const removeBlockRow = (rowIdx) =>
    setDraft((d) => ({
      ...d,
      schedule: DAYS.reduce(
        (acc, day) => ({ ...acc, [day.id]: (d.schedule[day.id] || []).filter((_, i) => i !== rowIdx) }),
        {},
      ),
    }));

  const rowCount = useMemo(() => {
    return Math.max(...DAYS.map((d) => (draft.schedule?.[d.id] || []).length), 1);
  }, [draft.schedule]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) { toast.error("Title required."); return; }
    setSaving(true);
    try {
      // Strip empty rows (all days blank) to keep data clean
      const cleanSchedule = { ...draft.schedule };
      for (const day of Object.keys(cleanSchedule)) {
        cleanSchedule[day] = (cleanSchedule[day] || []).map((b) => ({
          time: b.time || "",
          activity: b.activity || "",
          elds_codes: b.elds_codes || [],
        }));
      }
      const payload = { ...draft, schedule: cleanSchedule };
      if (editing) await api.put(`/lesson-plans/${editing}`, payload);
      else await api.post("/lesson-plans", payload);
      toast.success(editing ? "Lesson plan updated." : "Lesson plan created.");
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 pt-2" data-testid="lp-form">
      {/* Header fields */}
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-stone-500">Title</Label>
          <Input data-testid="lp-title" value={draft.title} onChange={(e) => up("title", e.target.value)} required className="mt-2 h-11" placeholder="e.g., Community Helpers Week" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Week of (Mon)</Label>
          <Input data-testid="lp-week" type="date" value={draft.week_of} onChange={(e) => up("week_of", mondayOf(e.target.value))} required className="mt-2 h-11" />
          <WeekPreview mondayIso={draft.week_of} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Age group</Label>
          <Select value={draft.age_group} onValueChange={(v) => up("age_group", v)}>
            <SelectTrigger data-testid="lp-age" className="mt-2 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AGE_GROUPS.map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Classroom (optional)</Label>
          <Input data-testid="lp-classroom" value={draft.classroom} onChange={(e) => up("classroom", e.target.value)} className="mt-2 h-11" placeholder="Preschool Room A" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-stone-500">Weekly objectives</Label>
          <Textarea data-testid="lp-objectives" value={draft.objectives} onChange={(e) => up("objectives", e.target.value)} rows={2} className="mt-2" placeholder="By end of week, children will…" />
        </div>
      </div>

      {/* Weekly grid */}
      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between bg-stone-50 border-b border-stone-200 px-4 py-3">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Weekly schedule · Monday → Friday</div>
          <Button type="button" data-testid="lp-add-row" variant="outline" size="sm" onClick={addBlockRow} className="rounded-xl">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="lp-schedule-grid">
            <thead>
              <tr className="bg-emerald-700 text-white">
                <th className="text-left text-xs uppercase tracking-widest px-3 py-2 w-10"></th>
                {DAYS.map((d) => (
                  <th key={d.id} className="text-left text-xs uppercase tracking-widest px-3 py-2">{d.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {Array.from({ length: rowCount }).map((_, rIdx) => (
                <tr key={rIdx} data-testid={`lp-row-${rIdx}`}>
                  <td className="p-2 align-top text-stone-300">
                    <button
                      type="button"
                      data-testid={`lp-remove-row-${rIdx}`}
                      onClick={() => removeBlockRow(rIdx)}
                      className="text-stone-400 hover:text-red-600"
                      title="Remove this time row"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </td>
                  {DAYS.map((d) => {
                    const block = (draft.schedule?.[d.id] || [])[rIdx] || { time: "", activity: "", elds_codes: [] };
                    return (
                      <td key={d.id} className="align-top p-2">
                        <Input
                          data-testid={`lp-time-${d.id}-${rIdx}`}
                          placeholder="9:00–10:00"
                          value={block.time || ""}
                          onChange={(e) => updateBlock(d.id, rIdx, { time: e.target.value })}
                          className="h-8 text-xs mb-1"
                        />
                        <Textarea
                          data-testid={`lp-activity-${d.id}-${rIdx}`}
                          placeholder="Activity (circle time, outdoor play, math centers…)"
                          value={block.activity || ""}
                          onChange={(e) => updateBlock(d.id, rIdx, { activity: e.target.value })}
                          rows={2}
                          className="text-xs resize-none"
                        />
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {(block.elds_codes || []).map((c) => (
                            <span
                              key={c}
                              data-testid={`lp-tag-${d.id}-${rIdx}-${c}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200"
                            >
                              {c}
                              <button
                                type="button"
                                onClick={() => updateBlock(d.id, rIdx, { elds_codes: (block.elds_codes || []).filter((x) => x !== c) })}
                                className="hover:text-red-700"
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </span>
                          ))}
                          <button
                            type="button"
                            data-testid={`lp-add-elds-${d.id}-${rIdx}`}
                            onClick={() => setEldsPickerFor({ day: d.id, idx: rIdx })}
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md text-emerald-700 hover:bg-emerald-50 border border-dashed border-emerald-300"
                          >
                            + ELDS
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Materials</Label>
          <Textarea data-testid="lp-materials" value={draft.materials} onChange={(e) => up("materials", e.target.value)} rows={2} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Notes / additional activities</Label>
          <Textarea data-testid="lp-activities" value={draft.activities} onChange={(e) => up("activities", e.target.value)} rows={2} className="mt-2" />
        </div>
      </div>

      <Button type="submit" data-testid="lp-submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : editing ? "Save changes" : "Save lesson plan"}
      </Button>

      {eldsPickerFor && (
        <ELDSPicker
          elds={elds}
          selected={(draft.schedule?.[eldsPickerFor.day] || [])[eldsPickerFor.idx]?.elds_codes || []}
          onClose={() => setEldsPickerFor(null)}
          onApply={(codes) => {
            updateBlock(eldsPickerFor.day, eldsPickerFor.idx, { elds_codes: codes });
            setEldsPickerFor(null);
          }}
        />
      )}
    </form>
  );
}

function ELDSPicker({ elds, selected, onClose, onApply }) {
  const [chosen, setChosen] = useState(new Set(selected || []));
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("all");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return (elds.standards || []).filter((s) => {
      if (domain !== "all" && s.domain !== domain) return false;
      if (!query) return true;
      return (
        s.code.toLowerCase().includes(query) ||
        s.label.toLowerCase().includes(query) ||
        s.strand.toLowerCase().includes(query)
      );
    });
  }, [elds, q, domain]);

  const toggle = (code) => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && typeof onClose === "function") onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lp-elds-picker">
        <DialogHeader>
          <DialogTitle>Tag Ohio ELDS indicators</DialogTitle>
          <DialogDescription>Pick one or more standards that this activity addresses.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400" />
            <Input data-testid="lp-elds-search" placeholder="Search by code, strand, or indicator…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" />
          </div>
          <Select value={domain} onValueChange={setDomain}>
            <SelectTrigger data-testid="lp-elds-domain" className="h-10 sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All domains</SelectItem>
              {(elds.domains || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="border border-stone-200 rounded-xl divide-y divide-stone-100 max-h-[50vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-stone-500">No standards match this search.</div>
          ) : filtered.map((s) => {
            const on = chosen.has(s.code);
            return (
              <button
                key={s.code}
                type="button"
                data-testid={`lp-elds-option-${s.code}`}
                onClick={() => toggle(s.code)}
                className={`w-full text-left px-4 py-2.5 flex gap-3 items-start hover:bg-stone-50 ${on ? "bg-emerald-50" : "bg-white"}`}
              >
                <input type="checkbox" readOnly checked={on} className="mt-1 accent-emerald-700" />
                <div className="flex-1">
                  <div className="text-xs font-mono font-bold text-emerald-700">{s.code}</div>
                  <div className="text-sm text-stone-800">{s.label}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">{s.strand}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-stone-500">{chosen.size} selected</div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button
              type="button"
              data-testid="lp-elds-apply"
              onClick={() => onApply(Array.from(chosen))}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
            >
              Apply {chosen.size} tag{chosen.size === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function LessonPlanGeneratorDialog({ open, onClose, onUse }) {
  const [form, setForm] = useState({
    classroom: "Preschool",
    age_group: "preschool_3_4",
    theme: "",
    week_of: mondayOf(new Date()),
  });
  const [step, setStep] = useState("setup"); // setup | titles | plan
  const [titles, setTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const CLASSROOMS = [
    { id: "Infants", label: "Infants (Birth–12m)", default_age: "infant" },
    { id: "Toddlers", label: "Toddlers (12–36m)", default_age: "toddler_older" },
    { id: "Preschool", label: "Preschool (2.5–5yrs)", default_age: "preschool_3_4" },
    { id: "School Age", label: "School-Age (5–12yrs)", default_age: "school_age" },
  ];

  const setClassroom = (id) => {
    const c = CLASSROOMS.find((x) => x.id === id);
    setForm((f) => ({ ...f, classroom: id, age_group: c?.default_age || f.age_group }));
  };

  const loadTitles = async () => {
    setLoadingTitles(true);
    setTitles([]);
    try {
      const res = await api.post("/lesson-plans/title-options", form);
      setTitles(res.data?.titles || []);
      setStep("titles");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingTitles(false);
    }
  };

  const generate = async (chosenTitle) => {
    setBusy(true);
    setSuggestion(null);
    try {
      const res = await api.post("/lesson-plans/generate", { ...form, theme: chosenTitle.title });
      setSuggestion({ ...res.data, title: chosenTitle.title });
      setStep("plan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resetAndClose = () => {
    setStep("setup"); setTitles([]); setSuggestion(null); if (typeof onClose === "function") onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lp-generator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-700" />
            AI starter lesson plan
          </DialogTitle>
          <DialogDescription>
            Step 1: pick a classroom + age group. Step 2: pick from 6 AI-suggested titles. Step 3: AI writes the full Mon–Fri plan — you tweak and save.
          </DialogDescription>
        </DialogHeader>

        {step === "setup" && (
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Classroom</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CLASSROOMS.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    data-testid={`lp-gen-classroom-${c.id.replace(/\s+/g, "-")}`}
                    onClick={() => setClassroom(c.id)}
                    className={`rounded-xl border px-3 py-3 text-left transition ${
                      form.classroom === c.id
                        ? "bg-emerald-700 text-white border-emerald-700"
                        : "bg-white text-stone-700 border-stone-200 hover:border-emerald-300"
                    }`}
                  >
                    <div className="text-sm font-semibold">{c.id}</div>
                    <div className="text-[10px] opacity-80">{c.label.split("(")[1]?.replace(")", "")}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Age group</Label>
                <Select value={form.age_group} onValueChange={(v) => setForm((f) => ({ ...f, age_group: v }))}>
                  <SelectTrigger data-testid="lp-gen-age" className="mt-2 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="infant">Infant (birth–12m)</SelectItem>
                    <SelectItem value="toddler_young">Young Toddler (12–24m)</SelectItem>
                    <SelectItem value="toddler_older">Older Toddler (24–36m)</SelectItem>
                    <SelectItem value="preschool_25_3">Preschool 2.5–3</SelectItem>
                    <SelectItem value="preschool_3_4">Preschool 3–4</SelectItem>
                    <SelectItem value="preschool_4_5">Preschool 4–5 (Pre-K)</SelectItem>
                    <SelectItem value="school_age">School-Age (5–12)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Week of (Mon)</Label>
                <Input data-testid="lp-gen-week" type="date" value={form.week_of} onChange={(e) => setForm((f) => ({ ...f, week_of: mondayOf(e.target.value) }))} className="mt-2 h-11" />
                <WeekPreview mondayIso={form.week_of} />
              </div>
            </div>
            <Button
              type="button"
              data-testid="lp-gen-load-titles"
              onClick={loadTitles}
              disabled={loadingTitles}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11"
            >
              {loadingTitles ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Brainstorming titles…</> : <><Sparkles className="w-4 h-4 mr-2" /> Show me title options</>}
            </Button>
            <p className="text-[11px] text-stone-500 text-center">Powered by GPT-5.1.</p>
          </div>
        )}

        {step === "titles" && (
          <div className="space-y-3 pt-2" data-testid="lp-gen-titles">
            <div className="flex items-center justify-between">
              <div className="text-xs text-stone-600">
                Pick one of these <strong>{titles.length}</strong> AI-suggested titles — the plan will be built around it.
              </div>
              <Button variant="ghost" size="sm" onClick={() => setStep("setup")} className="text-stone-500 text-xs">← Change classroom</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {titles.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`lp-gen-title-${i}`}
                  disabled={busy}
                  onClick={() => generate(t)}
                  className="text-left rounded-xl border border-stone-200 bg-white hover:border-emerald-500 hover:shadow-sm p-4 transition disabled:opacity-60"
                >
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">{t.theme || "Theme"}</div>
                  <div className="mt-1 font-display text-base font-medium text-stone-900">{t.title}</div>
                  <div className="mt-2 text-xs text-stone-500 line-clamp-3">{t.summary}</div>
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={loadTitles} disabled={loadingTitles} className="w-full rounded-xl">
              {loadingTitles ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Suggest 6 more titles</>}
            </Button>
            {busy && (
              <div className="flex items-center justify-center gap-2 text-emerald-700 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> AI is writing the plan…
              </div>
            )}
          </div>
        )}

        {step === "plan" && suggestion && (
          <div className="space-y-4 pt-2" data-testid="lp-gen-preview">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">Draft plan</div>
              <h4 className="mt-1 font-display text-lg text-stone-900">{suggestion.title}</h4>
              {suggestion.objectives && <p className="mt-2 text-sm text-stone-700">{suggestion.objectives}</p>}
            </div>
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
              <div className="grid grid-cols-5 divide-x divide-stone-100">
                {["monday", "tuesday", "wednesday", "thursday", "friday"].map((d) => (
                  <div key={d} className="p-2 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1.5">{d.slice(0, 3)}</div>
                    <div className="space-y-2">
                      {(suggestion.schedule?.[d] || []).map((b, i) => (
                        <div key={i} className="text-[11px] border border-stone-200 rounded-md p-2 bg-stone-50">
                          <div className="font-semibold text-emerald-800">{b.time}</div>
                          <div className="text-stone-800 mt-0.5 leading-snug">{b.activity}</div>
                          {(b.elds_codes || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-0.5">
                              {b.elds_codes.map((c) => (
                                <span key={c} className="text-[9px] font-mono font-bold text-emerald-700 bg-emerald-100 px-1 rounded">{c}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("titles")} className="rounded-xl flex-1">
                ← Pick a different title
              </Button>
              <Button
                data-testid="lp-gen-use"
                onClick={() => onUse(suggestion, form)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl flex-1"
              >
                Edit &amp; save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LessonPlanBatchDialog({ open, onClose, onCreated }) {
  const ALL_ROOMS = ["Infants", "Toddlers", "Preschool", "School Age"];
  const [weekOf, setWeekOf] = useState(mondayOf(new Date()));
  const [theme, setTheme] = useState("");
  const [selected, setSelected] = useState(new Set(ALL_ROOMS));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const toggle = (room) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(room)) next.delete(room); else next.add(room);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Pick at least one classroom."); return; }
    setBusy(true);
    setResults(null);
    try {
      const res = await api.post("/lesson-plans/generate-batch", {
        week_of: weekOf,
        theme: theme || undefined,
        classrooms: Array.from(selected),
      });
      setResults(res.data);
      toast.success(`Created ${res.data.created?.length || 0} lesson plan${res.data.created?.length === 1 ? "" : "s"}.`);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setResults(null); setBusy(false); if (typeof onClose === "function") onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lp-batch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-700" />
            Plan my whole center for the week
          </DialogTitle>
          <DialogDescription>
            One click — AI writes a full Mon–Fri lesson plan for every classroom you pick, all aligned to the same weekly theme.
          </DialogDescription>
        </DialogHeader>

        {!results ? (
          <div className="space-y-5 pt-2">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Week of (Mon)</Label>
                <Input
                  data-testid="lp-batch-week"
                  type="date"
                  value={weekOf}
                  onChange={(e) => setWeekOf(mondayOf(e.target.value))}
                  className="mt-2 h-11"
                />
                <WeekPreview mondayIso={weekOf} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Shared theme (optional)</Label>
                <Input
                  data-testid="lp-batch-theme"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder="Leave blank → AI picks one for you"
                  className="mt-2 h-11"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 mb-2 block">Classrooms to plan</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ALL_ROOMS.map((room) => {
                  const on = selected.has(room);
                  return (
                    <button
                      key={room}
                      type="button"
                      data-testid={`lp-batch-room-${room.replace(/\s+/g, "-")}`}
                      onClick={() => toggle(room)}
                      className={`rounded-xl border px-3 py-3 text-center transition ${
                        on ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-700 border-stone-200 hover:border-emerald-300"
                      }`}
                    >
                      <div className="text-sm font-semibold">{room}</div>
                      <div className="text-[10px] opacity-80 mt-0.5">{on ? "Selected" : "Skip"}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Heads-up:</strong> this generates {selected.size} plan{selected.size === 1 ? "" : "s"} in parallel and saves them immediately. You can edit or delete each one after. Takes about 30–40 seconds (4 rooms in parallel).
            </div>

            <Button
              data-testid="lp-batch-run"
              onClick={submit}
              disabled={busy || selected.size === 0}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing {selected.size} plan{selected.size === 1 ? "" : "s"}…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate all {selected.size} plan{selected.size === 1 ? "" : "s"}</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2" data-testid="lp-batch-results">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">Done · Week of {fmtDate(results.week_of)}</div>
              {results.shared_theme && (
                <div className="mt-1 text-sm text-stone-700">
                  Shared theme: <strong>{results.shared_theme}</strong>
                </div>
              )}
            </div>
            <ul className="space-y-2">
              {(results.created || []).map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white p-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">{p.classroom}</div>
                    <div className="font-medium text-stone-900">{p.title}</div>
                  </div>
                  <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                    Saved
                  </span>
                </li>
              ))}
              {(results.failed || []).map((p, i) => (
                <li key={i} className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">{p.classroom}</div>
                    <div className="text-sm text-red-900">Failed: {p.error}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setResults(null)} className="rounded-xl flex-1">
                Plan another week
              </Button>
              <Button onClick={close} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl flex-1" data-testid="lp-batch-close">
                Go to plans
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

